import React, { MutableRefObject, useRef } from "react";
import { useObserver } from "mobx-react";
import { Grid, TextField } from "@material-ui/core";
import {
  CustomerContractAgreementType,
  CustomerType,
  CustomerContractCompany,
  CustomerPicker_CustomerCardFragment
} from "@graphql";
import locale from "@bonliva-crm/locale";
import {
  Date,
  Text,
  Select,
  Checkbox,
  CurrencyText,
  ToggleButtonGroup
} from "@components/formx";
import CustomerSelector from "@containers/VirtualSelector/CustomerSelector/CustomerSelector";
import { CustomerContractEditorStoreContext } from "../../CustomerContractEditorStoreContext";
import { professions } from "@bonliva-crm/common";

export function ContractForm() {
  const prolongationDeadlineRef = useRef(null);
  const priceAdjustmentDeadlineRef = useRef(null);
  const editorStore = React.useContext(CustomerContractEditorStoreContext);

  function openNextDatePickerIfNeed(
    value: unknown,
    ref: MutableRefObject<{ ref: MutableRefObject<{ setOpen: (v: boolean) => void }> }>
  ) {
    if (!value) return;

    const datePicker = ref.current?.ref.current;

    if (datePicker) {
      datePicker.setOpen(true);
    }
  }

  return useObserver(() => (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <ToggleButtonGroup
          // ButtonProps={{ disabled: store.action === "duplicate" }}
          name="company"
          label={locale.CompanyName}
          options={Object.values(CustomerContractCompany).map((v) => ({
            label: locale.customerContractCompany[v],
            value: v
          }))}
        />
      </Grid>
      <Grid item xs={12}>
        <Select
          multi
          name="professions"
          label={locale.Profession}
          fullWidth
          options={editorStore.professions.toLabelValue((x) =>
            professions.getProfessionDisplayName(x)
          )}
          // disabled={!editorStore.isNew}
        />
      </Grid>
      <Grid item xs={12}>
        <Text label={locale.customerContractEdit.contractName} name="contractName" />
      </Grid>
      <Grid item xs={12}>
        <Text label={locale.customerContractEdit.contractNumber} name="contractNumber" />
      </Grid>
      <Grid item xs={6}>
        <Text label={locale.customerContractEdit.avtalsnummer} name="avtalsnummer" />
      </Grid>
      <Grid item xs={6}>
        <Text label={locale.customerContractEdit.avropsnummer} name="avropsnummer" />
      </Grid>
      <Grid item xs={12}>
        <Text
          label={locale.customerContractEdit.extraInvoiceLine}
          name="extraInvoiceLine"
        />
      </Grid>
      <Grid item xs={12}>
        <Select
          label={locale.customerContractEdit.agreementType}
          name="typeOfAgreement"
          options={Object.values(CustomerContractAgreementType).map((v) => ({
            label: locale.customerContractAgreementType[v],
            value: v
          }))}
          required
          error={!!editorStore.form.errors.typeOfAgreement}
        />
      </Grid>
      <Grid item xs={12}>
        <CustomerSelector
          disabledFilterFields={["customerType"]}
          fullWidth
          multi
          initialFilter={{
            customerType: [CustomerType.Region, CustomerType.Hospital]
          }}
          initialValues={
            (editorStore.form.values.orgCustomers.map((x) => ({
              ...x,
              _id: x.Id
            })) ?? []) as unknown as CustomerPicker_CustomerCardFragment[]
          }
          label={locale.SelectSomething(locale["Region customer"])}
          onChange={editorStore.setOrgCustomers as any}
          onRemoveItem={editorStore.removeOrgCustomer}
          required
          error={editorStore.form.errors.orgCustomers?.toString() || ""}
          value={(editorStore.form.values.orgCustomers || []).toLabelValue(
            (x) => x.Name,
            (x) => x.Id
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Date label={locale.customerContractEdit.dateStart} name="contractStart" utc />
      </Grid>
      <Grid item xs={12}>
        <Date label={locale.customerContractEdit.dateEnd} name="contractEnd" utc />
      </Grid>
      <Grid item xs={12}>
        <Date
          label={locale.customerContractEdit.prolongationDate}
          name="contractProlongation"
          onChangeListener={(v) => {
            openNextDatePickerIfNeed(v, prolongationDeadlineRef);
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Date
          ref={prolongationDeadlineRef}
          label={locale.customerContractEdit.prolongationDateDeadline}
          name="contractProlongationDeadline"
          placeholder={locale.customerContractEdit.prolongationDateDeadlinePlaceholder}
          DatePickerProps={{
            InputLabelProps: {
              shrink: true
            }
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Text
          label={locale.customerContractEdit.proLongationComment}
          name="prolongationComment"
          TextFieldProps={{
            disabled: editorStore.isProlongationDisabled,
            InputProps: { multiline: true }
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Date
          label={locale.customerContractEdit.priceAdjustmentDate}
          name="contractPriceAdjustmentDate"
          onChangeListener={(v) => {
            openNextDatePickerIfNeed(v, priceAdjustmentDeadlineRef);
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Date
          ref={priceAdjustmentDeadlineRef}
          label={locale.customerContractEdit.priceAdjustmentDateDeadline}
          name="contractPriceAdjustmentDateDeadline"
          placeholder={locale.customerContractEdit.priceAdjustmentDateDeadlinePlaceholder}
          DatePickerProps={{
            InputLabelProps: {
              shrink: true
            }
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Text
          label={locale.customerContractEdit.adjustmentComment}
          name="adjustmentComment"
          TextFieldProps={{
            disabled: editorStore.isProlongationDisabled,
            InputProps: { multiline: true }
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Date
          label={locale.customerContractEdit.currentEndDate}
          name="currentContractEnd"
        />
      </Grid>
      {editorStore.form.values.company === CustomerContractCompany.Bonliva_Care && (
        <Grid item xs={12}>
          <TextField
            label={
              editorStore.form?.errors?.dispenseDeadline ||
              locale.customerContractEdit.dispenseDeadline
            }
            name="dispenseDeadline"
            fullWidth
            error={!!editorStore.form.errors.dispenseDeadline}
            onChange={(e) => editorStore.setDispenseDeadline(e.target.value)}
            value={editorStore.form.values.dispenseDeadline}
            inputProps={{ type: "number", min: 1, max: 52, step: 1 }}
          />
        </Grid>
      )}

      <Grid item xs={3}>
        <CurrencyText
          type="number"
          label={locale.customerContractEdit.estimatedContractValue}
          name="estimatedContractValue"
          InputProps={{ type: "text" }}
          TextFieldProps={{ fullWidth: true, style: { maxWidth: 200 } }}
        />
      </Grid>
      <Grid item xs={12}>
        <Text
          label={locale.customerContractEdit.introductionInfo}
          name="introductionInfo"
          TextFieldProps={{ multiline: true }}
        />
      </Grid>

      <Grid item xs={12}>
        <Checkbox label={locale.AdditionalWork} name="overtime" />
        {editorStore.form.values.overtime && (
          <Text name="overtimeNote" TextFieldProps={{ multiline: true }} />
        )}
      </Grid>

      <Grid item xs={12}>
        <Checkbox
          label={locale.PossibleInvoiceForAccommodation}
          name="possibleInvoiceForAccommodation"
        />
        {editorStore.form.values.possibleInvoiceForAccommodation && (
          <Text
            name="possibleInvoiceForAccommodationNote"
            TextFieldProps={{ multiline: true }}
          />
        )}
      </Grid>

      <Grid item xs={12}>
        <Checkbox
          label={locale.PossibleInvoiceForTravel}
          name="possibleInvoiceForTravel"
        />
        {editorStore.form.values.possibleInvoiceForTravel && (
          <Text
            name="possibleInvoiceForTravelNote"
            TextFieldProps={{ multiline: true }}
          />
        )}
      </Grid>

      <Grid item xs={12}>
        <Checkbox label={locale.OvertimeOptionQuestion} name="overtimeOption" />
        {editorStore.form.values.overtimeOption && (
          <Text
            label={locale.OvertimeOptionOBNote}
            name="overtimeOptionOBNote"
            TextFieldProps={{ multiline: true }}
          />
        )}
      </Grid>
      <Grid item xs={12}>
        <Checkbox label={locale.Misc} name="otherOption" />
        {editorStore.form.values.otherOption && (
          <Text
            label={locale.OtherInfo}
            name="otherOptionNote"
            TextFieldProps={{ multiline: true }}
          />
        )}
      </Grid>
      <Grid item xs={12}>
        <Checkbox
          label={locale.customerContractEdit.useNationalProcurementInvoicing}
          name="useNationalProcurementInvoicing"
          CheckboxProps={{ disabled: editorStore.form.values.isNationalProcurement }}
        />
      </Grid>
    </Grid>
  ));
}
